/**
 * setupAxiosInterceptor — silent refresh-token rotation (POST-AIQ-042, Phase 2)
 *
 * Background (Phase 1 → Phase 2):
 *   Phase 1 (PR #164) shipped the *server side* of refresh-token rotation:
 *   `/auth/refresh` endpoint, RefreshToken row, family/jti tracking, reuse
 *   detection. But Login.js DROPPED the new `refresh_token` from the response
 *   and the access TTL stayed at 7 days, so Phase 1 actually reduced ZERO
 *   attack surface.
 *
 *   This module is Phase 2. It activates Phase 1's infrastructure by:
 *     1. catching 401 responses from any axios call,
 *     2. POSTing the stored refresh token to `/auth/refresh`,
 *     3. swapping in the new access token,
 *     4. retrying the original request once.
 *
 *   Coordinated with `backend/app/config.py` dropping
 *   `access_token_expire_minutes` 10080 → 60. Without the interceptor that
 *   TTL drop would log students out every hour; with it, they never see it.
 *
 * Three engineering invariants worth calling out:
 *
 *   (a) Single-flight refresh — non-negotiable. Without it, an app mount
 *       firing 6 parallel API calls all hit an expired access token, all
 *       fire concurrent /auth/refresh, the first one rotates and revokes
 *       the OLD refresh-token row, the other 5 then arrive with the
 *       now-revoked token → server detects reuse → REVOKES THE ENTIRE
 *       FAMILY → user is kicked out. The module-level `refreshInFlight`
 *       Promise serializes all callers onto a single network call.
 *
 *   (b) Per-request `_retried` flag — prevents an infinite-401 loop. If
 *       the rotated access token ALSO 401s (e.g. server-side tver bump
 *       between rotation + retry), we don't try again; we bubble the
 *       error up so the global 401 handler in App.js can logout.
 *
 *   (c) /auth/refresh + /auth/logout themselves skip the interceptor.
 *       Recursing into the rotation path from the rotation path is the
 *       definition of an infinite loop; logging out the user as a
 *       reaction to a logout 401 is incoherent.
 *
 * localStorage threat model:
 *   XSS that escapes DOMPurify still wins — that's tracked separately as
 *   POST-AIQ-074. Phase 2 doesn't claim immunity; it shrinks the blast
 *   radius from "7-day stolen token" to "60-min stolen token + 1-hour
 *   rotation window before the attacker can self-extend". A meaningful
 *   compression, not a fix.
 */

import axios from 'axios';
import { API_URL, SEND_ONCE } from './config';

// Module-level singleton. While one /auth/refresh call is in flight, every
// other 401 callback awaits THIS promise instead of firing its own request.
// Cleared in `.finally(...)` so a subsequent batch of 401s after a quiet
// period correctly starts a fresh refresh.
//
// Exported for tests only — production code MUST NOT reach into it.
let refreshInFlight = null;

export function _resetRefreshInFlightForTests() {
  refreshInFlight = null;
}

/**
 * Persist a {access, refresh} pair to localStorage in one place.
 * Centralized so we never accidentally drop one half of the pair
 * (the Phase-1 bug we're fixing here).
 */
export function persistTokens({ access_token, refresh_token }) {
  if (access_token) {
    localStorage.setItem('token', access_token);
  }
  if (refresh_token) {
    localStorage.setItem('refresh_token', refresh_token);
  }
}

/**
 * Clear both tokens from localStorage. Called from:
 *   - logout flow (App.js handleLogout)
 *   - reuse-detected branch (server says "this family is revoked")
 *
 * Note: does NOT clear `user` — the App component owns the user-shaped
 * state and clears its own copy. This function is intentionally
 * narrow: tokens only.
 */
export function clearTokens() {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
}

/**
 * This request's 401 is about the credential it carries, never the stored
 * session: no refresh may act on it, and no sign-out.
 *
 * Here, that is a request marked with config.js OWN_CREDENTIAL: a sign-in or
 * registration, a reset or verification token, a reset-link request.
 * shouldAttemptRefresh and App.js's 401 handler both ask this one question,
 * so the two cannot drift apart.
 */
export function carriesOwnCredential(config) {
  return config?.ownCredential === true;
}

/**
 * Decide whether a 401 from `url` should trigger refresh-rotation.
 *
 * /auth/refresh and /auth/logout MUST NOT trigger another /auth/refresh:
 *   - refresh 401 → reuse detected; we treat it as terminal logout below.
 *   - logout 401 → already on the way out; rotation is incoherent.
 *
 * Background-tagged requests already opt out of the App.js logout
 * handler; we mirror that here so a 401 on a background prefetch
 * doesn't push every other in-flight foreground request through a
 * refresh dance triggered by something the user can't see.
 */
function shouldAttemptRefresh(config) {
  if (!config || !config.url) return false;
  if (carriesOwnCredential(config)) return false;
  const url = String(config.url);
  if (url.includes('/auth/refresh')) return false;
  if (url.includes('/auth/logout')) return false;
  const headers = config.headers || {};
  if (
    headers['x-background-request'] === 'true' ||
    headers['X-Background-Request'] === 'true'
  ) {
    return false;
  }
  return true;
}

/**
 * Perform ONE refresh call. Wrapped by `getRefreshPromise()` for single-flight.
 *
 * Returns the new access token on success.
 * Rejects with an Error carrying `{ reuseDetected: true }` when the server
 * said the refresh token is bad (401 from /auth/refresh) — caller treats
 * that as terminal-logout-all.
 *
 * Why a fresh axios instance: the global axios has the response
 * interceptor attached, so recursing through it during a refresh would
 * loop forever. Using a clean instance also means the rotation request
 * never carries the now-likely-stale Authorization header.
 */
async function doRefresh() {
  // CROSS-TAB serialization (task #36, the owner's "password doesn't
  // stick"): every tab shares ONE refresh token in localStorage, and the
  // per-tab single-flight below cannot see another tab's in-flight refresh.
  // Two tabs waking from laptop sleep both present the same token; server-
  // side, the rotation loser reads as token THEFT (family nuke → every tab
  // logged out). navigator.locks serializes refresh ACROSS tabs so only one
  // rotation happens; the DOUBLE-CHECK inside doRefreshLocked is what makes
  // it correct — a tab that waited on the lock re-reads the access token and,
  // if a sibling already rotated, returns THAT token without a second network
  // rotation (so it never presents the now-stale refresh token at all, and
  // the reuse-detection nuke is never triggered). This is the PRIMARY fix and
  // it needs no server change — the server's strict RFC 9700 rotation is
  // unchanged (adversarial review PR #557: a server-side grace window that
  // minted a second in-family token silently broke /auth/logout and the
  // "detect theft within one cycle" guarantee, so it was dropped in favor of
  // fixing the race at its client source).
  //
  // Feature-detected. Browsers WITHOUT Web Locks (a shrinking tail — Chromium
  // 2018+, Firefox/Safari 2022+) fall back to the per-tab path and keep the
  // pre-existing behavior: on the rare simultaneous-wake race, the loser is
  // family-nuked and re-logs-in. That is a UX annoyance, never a security
  // hole — deliberately preferred over a server mechanism that carried one.
  const captured = localStorage.getItem('token');
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    // Bounded acquisition (review M3): a tab whose refresh request hangs
    // would otherwise hold the cross-tab lock and wedge every tab's auth.
    // Two bounds close it — the refresh POST times out (doRefreshLocked), so
    // the lock always releases; and acquisition waits at most 20s. On an
    // acquisition timeout we fall through to the per-tab path.
    const options = {};
    if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) {
      options.signal = AbortSignal.timeout(20000);
    }
    // `granted` (not the reject's error NAME) decides the fallback: the real
    // abort is a `TimeoutError` DOMException from AbortSignal.timeout(), NOT
    // an `AbortError` — matching on the name would miss it and surface a
    // spurious logout (review HIGH #3). If the callback never ran, acquisition
    // failed → retry per-tab; if it DID run, the error is a genuine refresh
    // failure and must propagate.
    let granted = false;
    try {
      return await navigator.locks.request(
        'liang-auth-refresh', options,
        () => { granted = true; return doRefreshLocked(captured); },
      );
    } catch (err) {
      if (!granted) return doRefreshLocked(captured);
      throw err;
    }
  }
  return doRefreshLocked(captured);
}

async function doRefreshLocked(capturedAccess) {
  // DOUBLE-CHECKED (review M4): a sibling tab may have rotated while we
  // waited on the cross-tab lock. If the access token changed since we
  // captured it before acquiring, redeem the sibling's result instead of
  // firing a second rotation with the now-stale refresh token (which the
  // server would read as reuse and nuke the family). This is what turns the
  // lock from "serialize N rotations" into "exactly one rotation, N readers".
  // `capturedAccess` is a localStorage value (string | null): the `current &&`
  // operand already excludes the no-prior-token case (a first-login refresh
  // captures null), so no separate presence check is needed.
  const current = localStorage.getItem('token');
  if (current && current !== capturedAccess) {
    return current;
  }

  const stored = localStorage.getItem('refresh_token');
  if (!stored) {
    // No refresh token to redeem — treat as not-logged-in.
    const err = new Error('no_refresh_token');
    err.reuseDetected = false;
    err.noRefreshToken = true;
    throw err;
  }

  let resp;
  try {
    resp = await axios.post(
      `${API_URL}/api/v1/auth/refresh`,
      { refresh_token: stored },
      {
        // Bypass the global response interceptor by using a fresh request
        // config; the interceptor checks the URL path, so we ALSO short-
        // circuit there. Belt-and-suspenders.
        headers: { 'Content-Type': 'application/json' },
        // Bounded (review M3): a hung refresh must settle so the cross-tab
        // Web Lock above always releases — 15s is generous for one token
        // rotation and strictly under the 20s acquisition bound (so a
        // wedged holder releases before its waiters give up).
        timeout: 15000,
        // SEND ONCE (code review of PR 1, 2026-10-23): a rotation is a write
        // the server cannot recognise as a repeat. App.js's axios-retry policy
        // re-sends a POST whose connection dropped — and a rotation whose
        // RESPONSE was lost would then be re-sent with the already-rotated
        // refresh token, which the server reads as token reuse and answers by
        // revoking the whole family (every tab logged out). Opting out leaves
        // the lost-response case to the next 401 → refresh cycle instead.
        ...SEND_ONCE,
      }
    );
  } catch (err) {
    // 401 from /auth/refresh = reuse detected / token invalid / family
    // revoked. Other failures (network, 5xx) bubble up unchanged.
    const status = err?.response?.status;
    if (status === 401) {
      const fatal = new Error('refresh_reuse_detected');
      fatal.reuseDetected = true;
      throw fatal;
    }
    throw err;
  }

  const { access_token, refresh_token } = resp.data || {};
  if (!access_token || !refresh_token) {
    // Server returned 200 but no tokens — defensive. Treat as fatal so
    // we don't end up looping with a missing Authorization header.
    const malformed = new Error('refresh_response_malformed');
    malformed.reuseDetected = false;
    throw malformed;
  }

  persistTokens({ access_token, refresh_token });
  return access_token;
}

/**
 * Single-flight wrapper around doRefresh().
 *
 * If a refresh is already in flight, return the SAME Promise so every
 * caller resolves with the SAME new token (or fails together on the
 * SAME reuse-detected error). Avoids the thundering-herd of N parallel
 * refresh attempts that would otherwise destroy the family on the very
 * first concurrent burst.
 *
 * Returns `{ promise, isFirst }`:
 *   - `promise` — what the caller awaits.
 *   - `isFirst` — true only for the caller that started the in-flight
 *     refresh. The reuse-detected branch uses this to fire
 *     `onReuseDetected` EXACTLY ONCE per failed refresh, regardless of
 *     how many concurrent requests were waiting on it. Without this,
 *     a 6-parallel-call burst that reuse-detects would fire the logout
 *     callback 6 times — UX-wise harmless if onReuseDetected is
 *     idempotent, but invariably callers do redirect-to-login work
 *     there and 6× navigation is a real bug.
 */
function getRefreshPromise() {
  if (refreshInFlight) {
    return { promise: refreshInFlight, isFirst: false };
  }
  refreshInFlight = doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return { promise: refreshInFlight, isFirst: true };
}

/**
 * Boot-time refresh (2026-10-21 — tester: "the page loaded logged in, then
 * switched to the login page"). App.js calls this when the STORED access
 * token is already past its `exp` (utils/accessTokenExpiry.js), so the
 * authenticated shell is never painted on a token that can only 401. It
 * shares the single-flight promise with the interceptor, so a request that
 * races the boot joins the same rotation instead of starting a second one.
 *
 * Resolves to ONE of three outcomes — never rejects:
 *   { status: 'refreshed', token }  — the rotation succeeded; tokens are
 *                                     already persisted (persistTokens).
 *   { status: 'terminal' }          — there is nothing to redeem: no refresh
 *                                     token, or the server rejected it (reuse
 *                                     detected / revoked family). Tokens are
 *                                     cleared here, exactly as the interceptor
 *                                     path does, so the caller lands on login.
 *   { status: 'transient' }         — the refresh could not be completed
 *                                     right now (offline, 5xx, malformed).
 *                                     Nothing is cleared: the caller keeps the
 *                                     stored session and the interceptor
 *                                     retries on the first 401, which is
 *                                     exactly today's behaviour.
 */
export async function refreshAccessTokenAtBoot() {
  if (!localStorage.getItem('refresh_token')) {
    return { status: 'terminal' };
  }
  const { promise, isFirst } = getRefreshPromise();
  try {
    const token = await promise;
    return { status: 'refreshed', token };
  } catch (err) {
    if (err?.reuseDetected || err?.noRefreshToken) {
      if (isFirst) clearTokens();
      return { status: 'terminal' };
    }
    return { status: 'transient' };
  }
}

/**
 * Install the global axios response interceptor.
 *
 * Returns an "eject" function so tests (and the App.js global handler)
 * can tear it down. In production it's installed once at module-init
 * via `index.js` and never ejected.
 *
 * @param {Object} opts
 * @param {Function} opts.onReuseDetected
 *    Called when the server says the refresh-token family is revoked.
 *    The App-level handler should clear in-memory user state and
 *    redirect to /login. localStorage tokens are already cleared here
 *    so the next page load shows the login screen.
 */
export function installAxiosRefreshInterceptor({ onReuseDetected } = {}) {
  const id = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      // Bubble up anything that isn't a 401 — 5xx must NOT trigger
      // refresh (those are server problems, not auth problems), and
      // non-axios errors don't carry a config to retry on.
      if (status !== 401 || !originalRequest) {
        return Promise.reject(error);
      }

      // Don't retry the same request twice. If a rotated access token
      // ALSO 401s (rare: tver bumped between rotation + retry), let
      // the original error bubble so App.js's logout handler can fire.
      if (originalRequest._retried) {
        return Promise.reject(error);
      }

      if (!shouldAttemptRefresh(originalRequest)) {
        return Promise.reject(error);
      }

      // No refresh token at all → user was never logged in (or
      // already logged out). Bubble up unchanged.
      if (!localStorage.getItem('refresh_token')) {
        return Promise.reject(error);
      }

      // Mark BEFORE awaiting refresh so concurrent failures from the
      // SAME originalRequest object (rare but possible with retry libs)
      // don't recurse.
      originalRequest._retried = true;

      const { promise: refreshPromise, isFirst } = getRefreshPromise();
      let newAccessToken;
      try {
        newAccessToken = await refreshPromise;
      } catch (refreshErr) {
        if (refreshErr?.reuseDetected && isFirst) {
          // Family revoked → terminal logout. Only the FIRST caller
          // to enter this branch does the cleanup work; subsequent
          // waiters see it's already done and just bubble the error.
          // Without the isFirst gate, a 6-parallel-call burst would
          // fire onReuseDetected 6 times → multiple redirect-to-login
          // navigations → broken UX.
          clearTokens();
          if (typeof onReuseDetected === 'function') {
            try {
              onReuseDetected();
            } catch (_) {
              // Never let an onReuseDetected throw mask the original
              // error; the user is being kicked out anyway.
            }
          }
        }
        // Either way (reuse-detected, network failure on refresh, or
        // malformed response), the original 401 cannot be resolved.
        // Reject with the ORIGINAL error so the caller's `.catch`
        // sees the same shape it would have without an interceptor.
        return Promise.reject(error);
      }

      // Swap the now-fresh access token onto THIS retry's config. This
      // per-request header covers the in-flight retry even when the original
      // request set its own Authorization (e.g. the App.js prefetch path).
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      // We deliberately do NOT mirror the token onto
      // axios.defaults.headers.common. persistTokens() above already wrote
      // the new access token to localStorage, and every SUBSEQUENT request
      // re-derives its header from localStorage via the request interceptor
      // (setupAxiosAuthHeader.js). Writing the global default would be a
      // latent security bug: axios flattens defaults.headers.common into a
      // request's config BEFORE the request-interceptor chain runs (see
      // core/Axios.js `_request`), so a lingering default satisfies that
      // interceptor's `hasExplicitAuth` guard and SHADOWS localStorage — a
      // token that outlives logout and bleeds across users on a shared
      // device. localStorage is the single source of truth for the bearer;
      // nothing else may hold an Authorization value.

      return axios(originalRequest);
    }
  );

  return () => axios.interceptors.response.eject(id);
}

export default installAxiosRefreshInterceptor;
